"use client";

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Flex,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  HStack,
  Button,
  useToast,
  Select,
  Tabs,
  TabList,
  Tab,
  VStack,
  Text,
  Badge,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  FormControl,
  FormLabel,
  Spinner,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  Grid,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FiPhone } from "react-icons/fi";
import { Trash2, ShoppingCart, MessageSquare } from "lucide-react";
import { CheckIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";
import SMSAgents from "../SMSAgents";
import SMSKnowledgeBase from "./SMSKnowledgeBase";
import A2PRegistrationDialog from "./A2PRegistrationDialog";
import { SendInitialSMSDialog } from "../sms-phone-numbers/SendInitialSMSDialog";
import { ConnectSMSNumberDialog } from "../sms-phone-numbers/ConnectSMSNumberDialog";
import {
  smsPhoneNumberService,
  type SMSPhoneNumber,
  type AvailablePhoneNumber,
  type A2PCampaign,
} from "../../sms-services/smsPhoneNumberService";
import { smsAgentService } from "../../sms-services/agentService";
import { useAuth } from "../../contexts/AuthContext";
import { useStableCredits } from "../../hooks/useStableCredits";
import { useDisclosure } from "@chakra-ui/react";
import { phoneNumberService } from "../../voice-services/phoneNumberService";
import { getDisplayPhonePrettyFromFields } from "../../voice-services/voicePhoneDisplay";
import { supabase } from "../../supabaseClient";

const MotionBox = motion(Box);

// ---- cache + abort pattern (same idea as VoiceAgents) ----
const CACHE_TTL_MS = 5 * 60 * 1000;

const SMSPhoneNumbers = () => {
  const [selectedSMSTab, setSelectedSMSTab] =
    React.useState<string>("phone-numbers");
  const [selectedAgentType, setSelectedAgentType] = React.useState<string>(
    () => localStorage.getItem("currentAgentType") || "SMS"
  );

  const [phoneNumbers, setPhoneNumbers] = useState<SMSPhoneNumber[]>([]);
  const [agents, setAgents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [searchResults, setSearchResults] = useState<AvailablePhoneNumber[]>(
    []
  );
  const [searchAreaCode, setSearchAreaCode] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const [selectedNumber, setSelectedNumber] = useState<SMSPhoneNumber | null>(
    null
  );
  const [phoneNumberToSMS, setPhoneNumberToSMS] =
    useState<SMSPhoneNumber | null>(null);

  const [isAddingToVoice, setIsAddingToVoice] = useState(false);
  const [voicePhoneNumbers, setVoicePhoneNumbers] = useState<string[]>([]);
  const [isLoadingVoiceNumbers, setIsLoadingVoiceNumbers] = useState(false);

  const [a2pCampaigns, setA2pCampaigns] = useState<A2PCampaign[]>([]);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [selectedCampaignKey, setSelectedCampaignKey] = useState<string>("");
  const [isConnectingCampaign, setIsConnectingCampaign] = useState(false);
  const [isDisconnectingCampaign, setIsDisconnectingCampaign] = useState(false);
  const [campaignsLoadedOnce, setCampaignsLoadedOnce] = useState(false);

  const toast = useToast();
  const navigate = useNavigate();
  const { isAdmin, currentClientId, clients, userProfile } = useAuth();
  const credits = useStableCredits();

  const {
    isOpen: isBuyModalOpen,
    onOpen: onBuyModalOpen,
    onClose: onBuyModalClose,
  } = useDisclosure();
  const {
    isOpen: isDetailsModalOpen,
    onOpen: onDetailsModalOpen,
    onClose: onDetailsModalClose,
  } = useDisclosure();
  const {
    isOpen: isA2PModalOpen,
    onOpen: onA2PModalOpen,
    onClose: onA2PModalClose,
  } = useDisclosure();
  const {
    isOpen: isSendSMSDialogOpen,
    onOpen: onSendSMSDialogOpen,
    onClose: onSendSMSDialogClose,
  } = useDisclosure();
  const {
    isOpen: isConnectSMSOpen,
    onOpen: onConnectSMSOpen,
    onClose: onConnectSMSClose,
  } = useDisclosure();

  const abortControllerRef = useRef<AbortController | null>(null);

  // Tenant/company context (same spirit as VoiceAgents)
  const companyName = useMemo(() => {
    const currentClient = clients.find((c) => c.id === currentClientId);
    return isAdmin && currentClient
      ? currentClient.company_name
      : userProfile?.company_name;
  }, [isAdmin, currentClientId, clients, userProfile]);

  const canBuyNumbers = ["LeadiumX", "Collective Solar"].includes(companyName ?? "");

  const getCacheKey = useCallback(
    (key: string) => `sms_phone_numbers_${key}_${companyName || "self"}`,
    [companyName]
  );

  const loadFromCache = useCallback(
    (key: string) => {
      try {
        const raw = localStorage.getItem(getCacheKey(key));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const age = Date.now() - (parsed.timestamp || 0);
        if (age > CACHE_TTL_MS) return null;
        return parsed.data ?? null;
      } catch {
        return null;
      }
    },
    [getCacheKey]
  );

  const saveToCache = useCallback(
    (key: string, data: any) => {
      try {
        localStorage.setItem(
          getCacheKey(key),
          JSON.stringify({ data, timestamp: Date.now() })
        );
      } catch {}
    },
    [getCacheKey]
  );

  // On tenant change: abort + clear UI + decide whether to show big loader
  useEffect(() => {
    // abort in-flight requests immediately
    if (abortControllerRef.current) abortControllerRef.current.abort();

    // clear tenant-specific UI so you never flash wrong data
    setPhoneNumbers([]);
    setAgents([]);
    setVoicePhoneNumbers([]);
    setSelectedNumber(null);

    // Only show big loader if nothing cached for this tenant
    const hasCachedNumbers = !!loadFromCache("phone_numbers");
    const hasCachedAgents = !!loadFromCache("agents");
    const hasCachedVoiceNumbers = !!loadFromCache("voice_phone_numbers");

    setIsLoading(
      !(hasCachedNumbers || hasCachedAgents || hasCachedVoiceNumbers)
    );
  }, [companyName, loadFromCache]);

  const fetchData = useCallback(async () => {
    // Abort any in-flight requests
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    // 1) Hydrate from cache for instant UI
    const cachedNumbers = loadFromCache("phone_numbers");
    const cachedAgents = loadFromCache("agents");
    const cachedVoiceNumbers = loadFromCache("voice_phone_numbers");

    if (cachedNumbers) setPhoneNumbers(cachedNumbers);
    if (cachedAgents) setAgents(cachedAgents);
    if (cachedVoiceNumbers) setVoicePhoneNumbers(cachedVoiceNumbers);

    try {
      // Ensure we have a session
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        if (!signal.aborted) setIsLoading(false);
        return;
      }

      // 2) Fetch fresh core data in parallel
      const [numbers, agentsList, voiceNumbers] = await Promise.all([
        smsPhoneNumberService.list(),
        smsAgentService.list(companyName || undefined),
        (async () => {
          if (process.env.REACT_APP_IS_MAIN_APP !== "true") return [] as any[];
          return phoneNumberService.list(companyName || undefined);
        })(),
      ]);

      if (signal.aborted) return;

      // filter SMS numbers for tenant
      let filteredNumbers = numbers;

      if (isAdmin) {
        const currentClient = clients.find((c) => c.id === currentClientId);
        if (currentClient) {
          filteredNumbers = numbers.filter(
            (number) => number.company_name === currentClient.company_name
          );
        }
      } else {
        const currentClient = clients.find((c) => c.id === currentClientId);
        if (currentClient) {
          filteredNumbers = numbers.filter(
            (number) => number.company_name === currentClient.company_name
          );
        }
      }

      setPhoneNumbers(filteredNumbers);
      saveToCache("phone_numbers", filteredNumbers);

      setAgents(agentsList);
      saveToCache("agents", agentsList);

      if (process.env.REACT_APP_IS_MAIN_APP === "true") {
        const voiceNumberStrings = (voiceNumbers || []).map(
          (num: any) => num.phone_number
        );
        setVoicePhoneNumbers(voiceNumberStrings);
        saveToCache("voice_phone_numbers", voiceNumberStrings);
      } else {
        setVoicePhoneNumbers([]);
        saveToCache("voice_phone_numbers", []);
      }

      setIsLoading(false);

      // 3) Non-blocking A2P status sync (best-effort)
      void (async () => {
        try {
          for (const number of filteredNumbers) {
            if (
              number.a2p_registration_status &&
              ["not_started", "submitted", "in_review"].includes(
                number.a2p_registration_status
              )
            ) {
              smsPhoneNumberService.syncA2PStatus(number.id).catch((error) => {
                console.error(
                  `[v0] Error syncing A2P status for ${number.phone_number}:`,
                  error
                );
              });
            }
          }
        } catch (e) {
          // silent
        }
      })();
    } catch (error: any) {
      if (error?.name === "AbortError") return;

      console.error("[v0] Error loading SMS phone numbers page:", error);
      toast({
        title: "Error loading data",
        description: error.message,
        status: "error",
        duration: 3000,
      });

      if (!signal.aborted) setIsLoading(false);
    }
  }, [
    clients,
    companyName,
    currentClientId,
    isAdmin,
    loadFromCache,
    saveToCache,
    toast,
  ]);

  // Keep this function (used by Add-to-Voice flow) but optimize + cache + abort-safe UI
  const loadVoicePhoneNumbers = useCallback(async () => {
    if (process.env.REACT_APP_IS_MAIN_APP !== "true") return;

    setIsLoadingVoiceNumbers(true);
    try {
      const voiceNumbers = await phoneNumberService.list(
        companyName || undefined
      );
      const voiceNumberStrings = voiceNumbers.map(
        (num: any) => num.phone_number
      );

      setVoicePhoneNumbers(voiceNumberStrings);
      saveToCache("voice_phone_numbers", voiceNumberStrings);
    } catch (error: any) {
      console.error("[v0] Error loading voice phone numbers:", error);
      toast({
        title: "Error loading voice numbers",
        description: error.message || "Failed to load voice phone numbers",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoadingVoiceNumbers(false);
    }
  }, [companyName, saveToCache, toast]);

  const loadA2PCampaigns = useCallback(async () => {
    setIsLoadingCampaigns(true);
    try {
      console.log("[v0] Fetching A2P campaigns from backend...");
      const campaigns = await smsPhoneNumberService.listA2PCampaigns();
      console.log(
        "[v0] Loaded A2P campaigns:",
        campaigns.length,
        "campaigns found",
        campaigns
      );
      setA2pCampaigns(campaigns);
    } catch (error: any) {
      console.error("[v0] Error loading A2P campaigns:", error);
      toast({
        title: "Error loading campaigns",
        description: error.message || "Failed to load A2P campaigns",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoadingCampaigns(false);
      setCampaignsLoadedOnce(true);
    }
  }, [toast]);

  // Selected campaign object derived from composite key
  const selectedCampaignObj = useMemo(() => {
    if (!selectedCampaignKey) return null;
    const [svcSid, campSid] = selectedCampaignKey.split("::");
    return (
      a2pCampaigns.find(
        (c) => c.messaging_service_sid === svcSid && c.usa2p_sid === campSid
      ) || null
    );
  }, [selectedCampaignKey, a2pCampaigns]);

  // Existing behavior: load only when on SMS + phone-numbers tab
  useEffect(() => {
    if (selectedAgentType === "SMS" && selectedSMSTab === "phone-numbers") {
      fetchData();
    }

    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [selectedAgentType, selectedSMSTab, fetchData]);

  // React to agent type change from other components
  useEffect(() => {
    const handleAgentTypeChangeEvent = (event: any) => {
      setSelectedAgentType(event.detail);
    };
    window.addEventListener("agentTypeChange", handleAgentTypeChangeEvent);
    return () => {
      window.removeEventListener("agentTypeChange", handleAgentTypeChangeEvent);
    };
  }, []);

  // When details modal opens, start loading A2P campaigns + refresh voice numbers
  useEffect(() => {
    if (!isDetailsModalOpen || !selectedNumber) return;

    // Reset campaigns state for this open
    setSelectedCampaignKey("");
    setCampaignsLoadedOnce(false);
    setA2pCampaigns([]);

    loadA2PCampaigns();

    if (process.env.REACT_APP_IS_MAIN_APP === "true") {
      loadVoicePhoneNumbers();
    }
  }, [
    isDetailsModalOpen,
    selectedNumber,
    loadA2PCampaigns,
    loadVoicePhoneNumbers,
  ]);

  const handleTabChange = (newTab: string) => {
    setSelectedSMSTab(newTab);
    localStorage.setItem("currentAgentTab", newTab);
    window.dispatchEvent(new CustomEvent("agentTabChange", { detail: newTab }));
  };

  const handleSearchNumbers = async () => {
    if (!searchAreaCode || searchAreaCode.length !== 3) {
      toast({
        title: "Invalid area code",
        description: "Please enter a 3-digit area code",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      setIsSearching(true);
      const results = await smsPhoneNumberService.search({
        country: "US",
        areaCode: searchAreaCode,
        sms: true,
        mms: true,
        limit: 20,
      });
      setSearchResults(results);

      if (results.length === 0) {
        toast({
          title: "No numbers found",
          description: `No available numbers found for area code ${searchAreaCode}`,
          status: "info",
          duration: 3000,
        });
      }
    } catch (error: any) {
      console.error("[v0] Error searching numbers:", error);
      toast({
        title: "Error searching numbers",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleBuyNumber = async (phoneNumber: string) => {
    try {
      await smsPhoneNumberService.buy({
        phoneNumber,
        country: "US",
      });

      toast({
        title: "Phone number purchased",
        description: `Successfully purchased ${phoneNumber}`,
        status: "success",
        duration: 3000,
      });

      onBuyModalClose();
      setSearchResults([]);
      setSearchAreaCode("");
      fetchData(); // refresh + cache
    } catch (error: any) {
      console.error("[v0] Error buying number:", error);
      toast({
        title: "Error purchasing number",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleDeleteNumber = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this phone number?")) {
      return;
    }

    try {
      await smsPhoneNumberService.delete(id);
      toast({
        title: "Phone number deleted",
        status: "success",
        duration: 3000,
      });
      fetchData();
    } catch (error: any) {
      console.error("[v0] Error deleting number:", error);
      toast({
        title: "Error deleting number",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleSendSMSClick = (
    phoneNumber: SMSPhoneNumber,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();

    // Check if A2P verified
    const isA2PVerified =
      phoneNumber.a2p_registration_status === "approved" ||
      phoneNumber.a2p_registration_status === "not_required";

    if (!isA2PVerified) {
      toast({
        title: "A2P verification required",
        description:
          "This phone number must be A2P verified before sending messages",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (!phoneNumber.agent_id) {
      toast({
        title: "No agent configured",
        description: "Please configure an agent for this phone number first",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    setPhoneNumberToSMS(phoneNumber);
    onSendSMSDialogOpen();
  };

  const handleAssignAgent = async (
    numberId: number,
    agentId: string | null
  ) => {
    const number = phoneNumbers.find((n) => n.id === numberId);
    if (number && agentId) {
      const isUSNumber =
        number.country === "US" && number.number_type === "local";
      const isA2PApproved = number.a2p_registration_status === "approved";
      const isA2PNotRequired =
        number.a2p_registration_status === "not_required";

      if (isUSNumber && !isA2PApproved && !isA2PNotRequired) {
        toast({
          title: "A2P Registration Required",
          description: `This US phone number must be A2P registered before it can be assigned to an agent. Current status: ${
            number.a2p_registration_status || "not_started"
          }`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    }

    try {
      await smsPhoneNumberService.assignAgent(numberId, { agentId });
      toast({
        title: "Agent assigned",
        status: "success",
        duration: 3000,
      });

      // Refresh numbers list
      fetchData();

      // Keep detail modal accurate if it's open
      if (selectedNumber?.id === numberId) {
        const updated = await smsPhoneNumberService.get(numberId);
        setSelectedNumber(updated);
      }
    } catch (error: any) {
      console.error("[v0] Error assigning agent:", error);
      toast({
        title: "Error assigning agent",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleAgentTypeChange = (type: string) => {
    setSelectedAgentType(type);
    localStorage.setItem("currentAgentType", type);
    if (type === "All") {
      navigate("/all-agents");
    } else if (type === "Voice") {
      navigate("/voice-agents");
    } else if (type === "SMS") {
      navigate("/sms-agents");
    } else if (type === "Email") {
      navigate("/email-agents");
    } else if (type === "WhatsApp") {
      navigate("/whatsapp-agents");
    }
  };

  const getAgentName = (agentId: string | undefined) => {
    if (!agentId) return "Not assigned";
    const agent = agents.find((a) => a.agent_id === agentId);
    return agent?.name || agentId;
  };

  const handleA2PRegistrationComplete = () => {
    onA2PModalClose();
    fetchData();
    if (selectedNumber) {
      smsPhoneNumberService.get(selectedNumber.id).then(setSelectedNumber);
    }
    toast({
      title: "A2P Registration Submitted",
      description: "Your A2P registration has been submitted for review.",
      status: "success",
      duration: 5000,
    });
  };

  const handleAddToVoiceNumbers = async () => {
    if (!selectedNumber) return;

    setIsAddingToVoice(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user) {
        throw new Error("No active session");
      }

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "user-id": session.user.id,
      };

      if (isAdmin && currentClientId) {
        const selectedClient = clients.find((c) => c.id === currentClientId);
        if (selectedClient) {
          headers["x-is-admin"] = "true";
          headers["x-company-name"] = selectedClient.company_name;
        }
      }

      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL || ""}/api/voice/add-sms-to-trunk`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            phone_number: selectedNumber.phone_number,
            trunk_sid: "TKffac7aafd7a77bdee2f93ce068c3f993",
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to add number to SIP trunk");
      }

      const result = await response.json();

      toast({
        title: "Successfully added as voice number!",
        status: "success",
        duration: 3000,
        position: "top-right",
      });

      // Reload (and cache) voice numbers to update button state
      await loadVoicePhoneNumbers();

      console.log("[v0] Successfully added SMS number as voice:", result);
    } catch (error) {
      console.error("[v0] Error adding to voice numbers:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to add as voice number",
        status: "error",
        duration: 5000,
        position: "top-right",
      });
    } finally {
      setIsAddingToVoice(false);
    }
  };

  const handleConnectToCampaign = async () => {
    if (!selectedNumber || !selectedCampaignKey) return;

    setIsConnectingCampaign(true);
    try {
      const [messagingServiceSid, campaignSid] =
        selectedCampaignKey.split("::");

      const campaign = a2pCampaigns.find(
        (c) =>
          c.messaging_service_sid === messagingServiceSid &&
          c.usa2p_sid === campaignSid
      );
      if (!campaign) throw new Error("Selected campaign not found");

      const statusLower = (campaign.campaign_status || "").toLowerCase();
      if (statusLower !== "verified") {
        toast({
          title: "Campaign not verified",
          description:
            "Only VERIFIED campaigns can be connected to a phone number.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
        setIsConnectingCampaign(false);
        return;
      }

      await smsPhoneNumberService.connectToCampaign(selectedNumber.id, {
        messagingServiceSid,
        campaignSid,
        campaignUseCase: campaign.use_case,
      });

      toast({
        title: "Successfully connected to campaign",
        description: `${getDisplayPhonePrettyFromFields(selectedNumber)} is now using the verified campaign`,
        status: "success",
        duration: 5000,
      });

      fetchData();
      const updated = await smsPhoneNumberService.get(selectedNumber.id);
      setSelectedNumber(updated);
      setSelectedCampaignKey("");
    } catch (error: any) {
      console.error("[v0] Error connecting to campaign:", error);
      toast({
        title: "Error connecting to campaign",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsConnectingCampaign(false);
    }
  };

  const handleDisconnectFromCampaign = async () => {
    if (!selectedNumber) return;

    try {
      setIsDisconnectingCampaign(true);
      const updatedNumber = await smsPhoneNumberService.disconnectFromCampaign(
        selectedNumber.id
      );
      setPhoneNumbers(
        phoneNumbers.map((num) =>
          num.id === updatedNumber.id ? updatedNumber : num
        )
      );
      setSelectedNumber(updatedNumber);
      toast({
        title: "Disconnected from campaign",
        description: "Phone number has been disconnected from the A2P campaign",
        status: "success",
        duration: 3000,
      });
      setCampaignsLoadedOnce(false);
      setA2pCampaigns([]);
      await loadA2PCampaigns();
    } catch (error: any) {
      console.error("Error disconnecting from campaign:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.error || "Failed to disconnect from campaign",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsDisconnectingCampaign(false);
    }
  };

  const isAlreadyVoiceNumber = selectedNumber
    ? voicePhoneNumbers.includes(selectedNumber.phone_number)
    : false;

  const activeNumbers = useMemo(
    () => phoneNumbers.filter((n) => n.is_active).length,
    [phoneNumbers]
  );
  const assignedNumbers = useMemo(
    () => phoneNumbers.filter((n) => n.agent_id).length,
    [phoneNumbers]
  );

  // ✅ determine if the selected number is currently connected to a VERIFIED campaign
  const isSelectedNumberConnectedToVerifiedCampaign = useMemo(() => {
    if (
      !selectedNumber?.a2p_campaign_sid ||
      !selectedNumber?.messaging_service_sid
    )
      return false;

    const match = a2pCampaigns.find(
      (c) =>
        c.usa2p_sid === selectedNumber.a2p_campaign_sid &&
        c.messaging_service_sid === selectedNumber.messaging_service_sid
    );

    return (match?.campaign_status || "").toLowerCase() === "verified";
  }, [selectedNumber, a2pCampaigns]);

  // ✅ campaigns shown in dropdown; if number is connected to a VERIFIED campaign,
  // exclude the currently-connected campaign from the list
  const campaignsForDropdown = useMemo(() => {
    if (!selectedNumber) return a2pCampaigns;

    const connectedKey = `${selectedNumber.messaging_service_sid || ""}::${
      selectedNumber.a2p_campaign_sid || ""
    }`;

    if (!isSelectedNumberConnectedToVerifiedCampaign) return a2pCampaigns;

    return a2pCampaigns.filter((c) => {
      const key = `${c.messaging_service_sid}::${c.usa2p_sid}`;
      return key !== connectedKey;
    });
  }, [
    a2pCampaigns,
    selectedNumber,
    isSelectedNumberConnectedToVerifiedCampaign,
  ]);

  if (selectedAgentType !== "SMS") return null;
  if (selectedSMSTab === "agents") return <SMSAgents />;
  if (selectedSMSTab === "knowledge-base") return <SMSKnowledgeBase />;

  return (
    <Box p={6} bg="white" minH="100vh">
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={4}>
          <Tabs
            index={
              selectedSMSTab === "agents"
                ? 0
                : selectedSMSTab === "phone-numbers"
                ? 1
                : 2
            }
            onChange={(index: number) => {
              const tabs = ["agents", "phone-numbers", "knowledge-base"];
              handleTabChange(tabs[index]);
            }}
            variant="solid-rounded"
            colorScheme="blackAlpha"
          >
            <TabList
              bg="gray.100"
              p={1}
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.300"
            >
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{
                  bg: "white",
                  color: "black",
                  boxShadow: "sm",
                }}
                _hover={{
                  bg: selectedSMSTab === "agents" ? "white" : "gray.200",
                }}
              >
                💬 SMS Agents
              </Tab>
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{
                  bg: "white",
                  color: "black",
                  boxShadow: "sm",
                }}
                _hover={{
                  bg: selectedSMSTab === "phone-numbers" ? "white" : "gray.200",
                }}
              >
                📞 SMS Phone Numbers
              </Tab>
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{
                  bg: "white",
                  color: "black",
                  boxShadow: "sm",
                }}
                _hover={{
                  bg:
                    selectedSMSTab === "knowledge-base" ? "white" : "gray.200",
                }}
              >
                📚 Knowledge Base
              </Tab>
            </TabList>
          </Tabs>
        </HStack>

        <HStack spacing={2}>
          <MotionBox
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            transition="0.15s ease"
          >
            <Select
              value={selectedAgentType}
              onChange={(e) => handleAgentTypeChange(e.target.value)}
              height="48px"
              bg="white"
              border="2px solid"
              borderColor="gray.200"
              borderRadius="12px"
              color="gray.800"
              fontWeight="semibold"
              fontSize="sm"
              pl="12px"
              pr="36px"
              cursor="pointer"
              shadow="sm"
              transition="all 0.2s ease"
              _hover={{
                borderColor: "primary.400",
                shadow: "md",
              }}
              _focus={{
                borderColor: "primary.600",
                boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.15)",
              }}
            >
              <option value="All">🤖 All Agents</option>
              <option value="Voice">📞 Voice Agents</option>
              <option value="SMS">💬 SMS Agents</option>

              {process.env.REACT_APP_IS_MAIN_APP === "true" && (
                <option value="Email">📧 Email Agents</option>
              )}

              <option value="WhatsApp">📱 WhatsApp Agents</option>
            </Select>
          </MotionBox>

          {canBuyNumbers && (
            <Button
            onClick={onBuyModalOpen}
            variant="brandGradient"
            fontWeight="medium"
            leftIcon={<ShoppingCart size={16} />}
            isDisabled={credits !== null && credits < 0.01}
            title={
              credits !== null && credits < 0.01
                ? "Insufficient balance to buy a phone number"
                : undefined
            }
          >
            Buy Phone Number
          </Button>
          )}

          <Button
            leftIcon={<ShoppingCart size={16} />}
            onClick={onConnectSMSOpen}
            variant="outline"
            size="md"
            colorScheme="blue"
            fontWeight="medium"
          >
            Connect Your Number
          </Button>
        </HStack>
      </Flex>

      {!isLoading && phoneNumbers.length > 0 && (
        <Grid
          templateColumns="repeat(auto-fit, minmax(200px, 1fr))"
          gap={4}
          mb={6}
        >
          <Card
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px) scale(1.02)",
              shadow: "xl",
            }}
          >
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Total Numbers</StatLabel>
                <StatNumber color="black">{activeNumbers}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px) scale(1.02)",
              shadow: "xl",
            }}
          >
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Assigned to Agents</StatLabel>
                <StatNumber color="black">{assignedNumbers}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px) scale(1.02)",
              shadow: "xl",
            }}
          >
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Unassigned</StatLabel>
                <StatNumber color="black">
                  {activeNumbers - assignedNumbers}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </Grid>
      )}

      {/* Loading / Empty / List */}
      {isLoading ? (
        <Flex
          justify="center"
          align="center"
          h="500px"
          minH="calc(100vh - 80px)"
        >
          <Spinner size="xl" color="blue.500" />
        </Flex>
      ) : phoneNumbers.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          h="500px"
          minH="calc(100vh - 80px)"
          bg="gray.50"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
        >
          <FiPhone size={48} color="gray" />
          <Text fontSize="lg" fontWeight="medium" color="gray.600" mt={4}>
            No SMS phone numbers yet
          </Text>
          <Text fontSize="sm" color="gray.500" mt={2}>
            Get started by purchasing your first SMS phone number
          </Text>
          <Button
            onClick={onBuyModalOpen}
            colorScheme="primary"
            bg="primary.800"
            _hover={{ bg: "primary.900" }}
            leftIcon={<ShoppingCart size={16} />}
            mt={6}
            isDisabled={credits !== null && credits < 0.01}
            title={
              credits !== null && credits < 0.01
                ? "Insufficient balance to buy a phone number"
                : undefined
            }
          >
            Buy Phone Number
          </Button>
        </Flex>
      ) : (
        <Grid templateColumns="repeat(auto-fill, minmax(400px, 1fr))" gap={6}>
          {phoneNumbers.map((number) => (
            <Card
              role="button"
              tabIndex={credits !== null && credits < 0.01 ? -1 : 0}
              aria-disabled={credits !== null && credits < 0.01}
              key={number.id}
              borderLeft="4px"
              borderLeftColor="blue.500"
              cursor={
                credits !== null && credits < 0.01 ? "not-allowed" : "pointer"
              }
              onClick={() => {
                if (credits !== null && credits < 0.01) return;
                setSelectedNumber(number);
                onDetailsModalOpen();
              }}
              _hover={credits !== null && credits < 0.01 ? {} : { shadow: "md" }}
              height="250px"
              display="flex"
              flexDirection="column"
              opacity={credits !== null && credits < 0.01 ? 0.5 : 1}
              transition="all 0.2s"
              title={
                credits !== null && credits < 0.01
                  ? "Insufficient balance to view phone number details"
                  : undefined
              }
            >
              <CardBody display="flex" flexDirection="column" p={5}>
                <VStack align="stretch" spacing={3} flex={1}>
                  <Flex justify="space-between" align="start">
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontSize="lg" fontWeight="bold" color="black">
                        {getDisplayPhonePrettyFromFields(number)}
                      </Text>
                    </VStack>
                    <HStack spacing={1}>
                      <IconButton
                        aria-label="Send initial SMS"
                        icon={<MessageSquare size={16} />}
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={(e) => handleSendSMSClick(number, e)}
                        isDisabled={
                          !(
                            number.a2p_registration_status === "approved" ||
                            number.a2p_registration_status === "not_required"
                          ) || !number.agent_id
                        }
                        title={
                          !(
                            number.a2p_registration_status === "approved" ||
                            number.a2p_registration_status === "not_required"
                          )
                            ? "A2P verification required"
                            : !number.agent_id
                            ? "Assign an agent first"
                            : "Send initial SMS"
                        }
                      />
                      <IconButton
                        aria-label="Delete"
                        icon={<Trash2 size={16} />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNumber(number.id);
                        }}
                      />
                    </HStack>
                  </Flex>

                  <VStack
                    align="stretch"
                    spacing={2}
                    pt={2}
                    borderTop="1px solid"
                    borderColor="gray.100"
                  >
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" color="gray.600">
                        Agent:
                      </Text>
                      <Badge
                        colorScheme={number.agent_id ? "green" : "gray"}
                        fontSize="xs"
                      >
                        {getAgentName(number.agent_id)}
                      </Badge>
                    </Flex>

                    {number.a2p_registration_status &&
                      number.a2p_registration_status !== "not_started" && (
                        <Flex justify="space-between" align="center">
                          <Text fontSize="sm" color="gray.600">
                            A2P Status:
                          </Text>
                          <Badge
                            colorScheme={
                              number.a2p_registration_status === "approved"
                                ? "green"
                                : number.a2p_registration_status === "rejected"
                                ? "red"
                                : "yellow"
                            }
                            fontSize="xs"
                          >
                            {number.a2p_registration_status}
                          </Badge>
                        </Flex>
                      )}
                  </VStack>

                  <Text
                    fontSize="xs"
                    color="gray.400"
                    pt={2}
                    borderTop="1px solid"
                    borderColor="gray.100"
                    mt="auto"
                  >
                    Provider:{" "}
                    {(() => {
                      const domain = process.env.REACT_APP_DOMAIN_NAME || "";
                      const parts = domain.split(".");
                      if (parts.length === 2) {
                        return (
                          parts[0].charAt(0).toUpperCase() + parts[0].slice(1)
                        );
                      } else if (parts.length === 3) {
                        return (
                          parts[1].charAt(0).toUpperCase() + parts[1].slice(1)
                        );
                      } else {
                        return "Custom Telephony";
                      }
                    })()}
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </Grid>
      )}

      {/* Buy Modal */}
      <Modal isOpen={isBuyModalOpen} onClose={onBuyModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Buy SMS Phone Number</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Area Code</FormLabel>
                <HStack>
                  <Input
                    placeholder="e.g., 415"
                    value={searchAreaCode}
                    onChange={(e) => setSearchAreaCode(e.target.value)}
                    maxLength={3}
                  />
                  <Button
                    onClick={handleSearchNumbers}
                    isLoading={isSearching}
                    colorScheme="primary"
                    bg="primary.800"
                    _hover={{ bg: "primary.900" }}
                  >
                    Search
                  </Button>
                </HStack>
              </FormControl>

              {searchResults.length > 0 && (
                <VStack
                  spacing={2}
                  align="stretch"
                  maxH="400px"
                  overflowY="auto"
                >
                  {searchResults.map((result) => (
                    <Card key={result.phoneNumber}>
                      <CardBody>
                        <Flex justify="space-between" align="center">
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="bold">{result.phoneNumber}</Text>
                            <Text fontSize="sm" color="gray.600">
                              {result.locality}, {result.region}
                            </Text>
                          </VStack>
                          <Button
                            size="sm"
                            colorScheme="primary"
                            bg="primary.800"
                            _hover={{ bg: "primary.900" }}
                            onClick={() => handleBuyNumber(result.phoneNumber)}
                          >
                            Buy
                          </Button>
                        </Flex>
                      </CardBody>
                    </Card>
                  ))}
                </VStack>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={onBuyModalClose}
              variant="ghost"
              color="black"
              _hover={{ bg: "gray.100" }}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Details Modal */}
      <Modal
        isOpen={isDetailsModalOpen}
        onClose={onDetailsModalClose}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack justify="space-between" align="center">
              <Text>Phone Number Details</Text>
              <HStack spacing={2}>
                <IconButton
                  aria-label="Send initial SMS"
                  icon={<MessageSquare size={16} />}
                  onClick={() => {
                    if (selectedNumber) {
                      setPhoneNumberToSMS(selectedNumber);
                      onSendSMSDialogOpen();
                    }
                  }}
                  variant="outline"
                  colorScheme="green"
                  color={"green.500"}
                  _hover={{ bg: "gray.200" }}
                  size="sm"
                  isDisabled={
                    !selectedNumber ||
                    !(
                      selectedNumber.a2p_registration_status === "approved" ||
                      selectedNumber.a2p_registration_status === "not_required"
                    ) ||
                    !selectedNumber.agent_id
                  }
                  title={
                    !selectedNumber
                      ? ""
                      : !(
                          selectedNumber.a2p_registration_status ===
                            "approved" ||
                          selectedNumber.a2p_registration_status ===
                            "not_required"
                        )
                      ? "A2P verification required"
                      : !selectedNumber.agent_id
                      ? "Assign an agent first"
                      : "Send initial SMS"
                  }
                />
                <IconButton
                  aria-label="Delete phone number"
                  icon={<Trash2 size={16} />}
                  onClick={() => {
                    if (selectedNumber) {
                      onDetailsModalClose();
                      handleDeleteNumber(selectedNumber.id);
                    }
                  }}
                  variant="outline"
                  colorScheme="red"
                  color={"red.500"}
                  _hover={{ bg: "gray.200" }}
                  size="sm"
                />
              </HStack>
            </HStack>
          </ModalHeader>

          <ModalBody>
            {selectedNumber && (
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>Phone Number</FormLabel>
                  <Input
                    value={getDisplayPhonePrettyFromFields(selectedNumber)}
                    isReadOnly
                  />
                </FormControl>

                {/* A2P Campaign Management */}
                <Alert
                  status={
                    !campaignsLoadedOnce || isLoadingCampaigns
                      ? "info"
                      : a2pCampaigns.length > 0
                      ? "info"
                      : "warning"
                  }
                >
                  <AlertIcon />
                  <Box flex="1">
                    <AlertTitle>A2P Campaign Management</AlertTitle>
                    <AlertDescription>
                      <VStack align="stretch" spacing={3} mt={2}>
                        {/* ✅ MOVED HERE: A2P Registration Required warning */}
                        {selectedNumber.country === "US" &&
                          selectedNumber.number_type === "local" &&
                          selectedNumber.a2p_registration_status !==
                            "approved" &&
                          selectedNumber.a2p_registration_status !==
                            "in_review" &&
                          selectedNumber.a2p_registration_status !==
                            "not_required" && (
                            <Box
                              p={3}
                              bg="yellow.50"
                              borderRadius="md"
                              borderWidth="1px"
                              borderColor="yellow.200"
                            >
                              <Text
                                fontSize="sm"
                                fontWeight="bold"
                                color="yellow.800"
                              >
                                A2P Registration Required
                              </Text>

                              <Text fontSize="sm" color="yellow.700" mt={1}>
                                This US phone number must be A2P registered
                                before it can be assigned to an agent. Current
                                status:{" "}
                                <Badge
                                  ml={1}
                                  colorScheme={
                                    selectedNumber.a2p_registration_status ===
                                    "rejected"
                                      ? "red"
                                      : selectedNumber.a2p_registration_status ===
                                        "submitted"
                                      ? "yellow"
                                      : "gray"
                                  }
                                >
                                  {selectedNumber.a2p_registration_status ||
                                    "not_started"}
                                </Badge>
                              </Text>

                              {(!selectedNumber.a2p_registration_status ||
                                selectedNumber.a2p_registration_status ===
                                  "not_started") && (
                                <Button
                                  mt={3}
                                  colorScheme="primary"
                                  bg="primary.800"
                                  _hover={{ bg: "primary.900" }}
                                  size="sm"
                                  onClick={() => {
                                    onDetailsModalClose();
                                    onA2PModalOpen();
                                  }}
                                >
                                  Start A2P Registration
                                </Button>
                              )}
                            </Box>
                          )}

                        {/* Campaign content */}
                        {!campaignsLoadedOnce || isLoadingCampaigns ? (
                          <HStack spacing={3} py={2}>
                            <Spinner size="sm" color="blue.500" />
                            <Text fontSize="sm" color="gray.600">
                              Loading campaigns from Twilio...
                            </Text>
                          </HStack>
                        ) : a2pCampaigns.length === 0 ? (
                          <VStack align="stretch" spacing={2}>
                            <Text fontSize="sm" color="gray.700">
                              No verified A2P campaigns found. You need at least
                              one VERIFIED campaign to connect phone numbers.
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              To create a campaign:
                            </Text>
                            <Box pl={4}>
                              <Text fontSize="sm" color="gray.600">
                                1. Click "Start A2P Registration" below to
                                create your first campaign
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                2. Wait for Twilio to verify your campaign
                                (usually 1-2 business days)
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                3. Once verified, you can connect any phone
                                number to this campaign
                              </Text>
                            </Box>
                            <Button
                              mt={2}
                              size="sm"
                              colorScheme="blue"
                              leftIcon={<span>🔄</span>}
                              onClick={() => {
                                setCampaignsLoadedOnce(false);
                                setA2pCampaigns([]);
                                loadA2PCampaigns();
                              }}
                            >
                              Refresh Campaign List
                            </Button>
                          </VStack>
                        ) : (
                          <VStack align="stretch" spacing={3}>
                            {selectedNumber?.a2p_campaign_sid &&
                              selectedNumber?.messaging_service_sid && (
                                <Box
                                  p={3}
                                  bg="green.50"
                                  borderRadius="md"
                                  borderWidth="1px"
                                  borderColor="green.200"
                                >
                                  <HStack justify="space-between" align="start">
                                    <VStack
                                      align="stretch"
                                      spacing={1}
                                      flex="1"
                                    >
                                      <Text
                                        fontSize="sm"
                                        fontWeight="bold"
                                        color="green.800"
                                      >
                                        Currently Connected Campaign{" "}
                                        <Badge
                                          colorScheme={
                                            selectedNumber.a2p_registration_status ===
                                            "approved"
                                              ? "green"
                                              : selectedNumber.a2p_registration_status ===
                                                "rejected"
                                              ? "red"
                                              : selectedNumber.a2p_registration_status ===
                                                  "submitted" ||
                                                selectedNumber.a2p_registration_status ===
                                                  "in_review"
                                              ? "yellow"
                                              : "gray"
                                          }
                                        >
                                          {selectedNumber.a2p_registration_status ||
                                            "not_started"}
                                        </Badge>
                                      </Text>

                                      <Text fontSize="xs" color="green.700">
                                        {selectedNumber.messaging_service_sid +
                                          " - " +
                                          (selectedNumber.a2p_campaign_use_case ||
                                            "Unknown Use Case")}
                                      </Text>
                                    </VStack>
                                    <Button
                                      size="sm"
                                      colorScheme="red"
                                      variant="outline"
                                      onClick={handleDisconnectFromCampaign}
                                      isLoading={isDisconnectingCampaign}
                                    >
                                      Disconnect
                                    </Button>
                                  </HStack>
                                </Box>
                              )}

                            <Box>
                              <Text fontSize="sm" mb={2}>
                                {selectedNumber?.a2p_campaign_sid
                                  ? `${
                                      campaignsForDropdown.length
                                    } other campaign${
                                      campaignsForDropdown.length !== 1
                                        ? "s"
                                        : ""
                                    } available. Switch to a different campaign:`
                                  : `${a2pCampaigns.length} campaign${
                                      a2pCampaigns.length > 1 ? "s" : ""
                                    } available. Connect this number to ${
                                      a2pCampaigns.filter(
                                        (c) =>
                                          (
                                            c.campaign_status || ""
                                          ).toLowerCase() === "verified"
                                      ).length > 0
                                        ? "a verified campaign"
                                        : "an existing campaign"
                                    }:`}
                              </Text>

                              <Select
                                placeholder="Select a campaign"
                                value={selectedCampaignKey}
                                onChange={(e) =>
                                  setSelectedCampaignKey(e.target.value)
                                }
                                size="sm"
                              >
                                {campaignsForDropdown.map((camp) => {
                                  const statusDisplay =
                                    camp.campaign_status?.toUpperCase() ||
                                    "UNKNOWN";
                                  const key = `${camp.messaging_service_sid}::${camp.usa2p_sid}`;

                                  return (
                                    <option key={key} value={key}>
                                      [{statusDisplay}]{" "}
                                      {camp.messaging_service_sid} -{" "}
                                      {camp.use_case}
                                    </option>
                                  );
                                })}
                              </Select>

                              {selectedCampaignKey &&
                                (() => {
                                  const [svcSid, campSid] =
                                    selectedCampaignKey.split("::");
                                  const campaign = a2pCampaigns.find(
                                    (c) =>
                                      c.messaging_service_sid === svcSid &&
                                      c.usa2p_sid === campSid
                                  );
                                  return campaign &&
                                    campaign.campaign_status?.toLowerCase() !==
                                      "verified" ? (
                                    <Alert status="warning" size="sm" mt={2}>
                                      <AlertIcon />
                                      <Text fontSize="xs">
                                        This campaign status is{" "}
                                        <Badge
                                          colorScheme={
                                            campaign.campaign_status ===
                                            "failed"
                                              ? "red"
                                              : campaign.campaign_status ===
                                                  "in-review" ||
                                                campaign.campaign_status ===
                                                  "pending"
                                              ? "yellow"
                                              : "gray"
                                          }
                                          fontSize="xs"
                                        >
                                          {campaign.campaign_status ||
                                            "unknown"}
                                        </Badge>
                                        . Only verified campaigns can send
                                        messages.
                                      </Text>
                                    </Alert>
                                  ) : null;
                                })()}

                              <Button
                                size="sm"
                                colorScheme="primary"
                                bg="primary.800"
                                _hover={{ bg: "primary.900" }}
                                onClick={handleConnectToCampaign}
                                isLoading={isConnectingCampaign}
                                isDisabled={!selectedCampaignKey}
                                mt={2}
                              >
                                {selectedNumber?.a2p_campaign_sid
                                  ? "Switch Campaign"
                                  : "Connect to Campaign"}
                              </Button>
                            </Box>
                          </VStack>
                        )}
                      </VStack>
                    </AlertDescription>
                  </Box>
                </Alert>

                {selectedNumber.a2p_registration_status === "rejected" &&
                  selectedNumber.a2p_registration_error_details && (
                    <Alert status="error" alignItems="flex-start">
                      <AlertIcon />
                      <Box flex="1">
                        <AlertTitle>A2P Registration Rejected</AlertTitle>
                        <AlertDescription>
                          {selectedNumber.a2p_registration_error_details
                            ?.errors &&
                            Array.isArray(
                              selectedNumber.a2p_registration_error_details
                                .errors
                            ) && (
                              <VStack align="stretch" mt={2} spacing={1}>
                                {selectedNumber.a2p_registration_error_details.errors.map(
                                  (error: any, idx: number) => (
                                    <Box
                                      key={idx}
                                      fontSize="sm"
                                      pl={2}
                                      borderLeft="2px solid"
                                      borderColor="red.300"
                                    >
                                      {error.error_code && (
                                        <Text
                                          as="span"
                                          fontWeight="bold"
                                          color="red.700"
                                        >
                                          [{error.error_code}]
                                        </Text>
                                      )}{" "}
                                      {error.failure_reason ||
                                        error.description ||
                                        "Unknown error"}
                                    </Box>
                                  )
                                )}
                              </VStack>
                            )}
                          {selectedNumber.a2p_rejected_at && (
                            <Text fontSize="xs" color="gray.500" mt={2}>
                              Rejected on:{" "}
                              {new Date(
                                selectedNumber.a2p_rejected_at
                              ).toLocaleString()}
                            </Text>
                          )}
                          <Button
                            mt={3}
                            colorScheme="primary"
                            bg="primary.800"
                            _hover={{ bg: "primary.900" }}
                            size="sm"
                            onClick={() => {
                              onDetailsModalClose();
                              onA2PModalOpen();
                            }}
                          >
                            Resubmit A2P Registration
                          </Button>
                        </AlertDescription>
                      </Box>
                    </Alert>
                  )}

                <FormControl>
                  <FormLabel>Assign to Agent</FormLabel>
                  <Select
                    value={selectedNumber.agent_id || ""}
                    onChange={(e) =>
                      handleAssignAgent(
                        selectedNumber.id,
                        e.target.value || null
                      )
                    }
                    isDisabled={
                      selectedNumber.country === "US" &&
                      selectedNumber.number_type === "local" &&
                      selectedNumber.a2p_registration_status !== "approved" &&
                      selectedNumber.a2p_registration_status !== "not_required"
                    }
                  >
                    <option value="">Not assigned</option>
                    {agents.map((agent) => (
                      <option key={agent.agent_id} value={agent.agent_id}>
                        {agent.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Capabilities</FormLabel>
                  <HStack>
                    <Badge colorScheme="blue">SMS</Badge>
                  </HStack>
                </FormControl>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            {process.env.REACT_APP_IS_MAIN_APP === "true" && (
              <Button
                onClick={handleAddToVoiceNumbers}
                colorScheme="primary"
                variant="outline"
                color="primary.900"
                _hover={{ bg: "primary.50" }}
                mr={3}
                isLoading={isAddingToVoice || isLoadingVoiceNumbers}
                loadingText="Adding..."
                isDisabled={isAlreadyVoiceNumber}
                leftIcon={isAlreadyVoiceNumber ? <CheckIcon /> : undefined}
              >
                {isAlreadyVoiceNumber
                  ? "Already Added as Voice"
                  : "Add as Voice Number"}
              </Button>
            )}
            <Button
              onClick={onDetailsModalClose}
              colorScheme="primary"
              bg="primary.800"
              _hover={{ bg: "primary.900" }}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {phoneNumberToSMS && (
        <SendInitialSMSDialog
          isOpen={isSendSMSDialogOpen}
          onClose={onSendSMSDialogClose}
          phoneNumber={phoneNumberToSMS.phone_number}
          agentId={phoneNumberToSMS.agent_id || null}
          initialMessage={
            phoneNumberToSMS.agent_id
              ? agents.find((a) => a.agent_id === phoneNumberToSMS.agent_id)
                  ?.initial_message ?? ""
              : ""
          }
        />
      )}

      {selectedNumber && (
        <A2PRegistrationDialog
          isOpen={isA2PModalOpen}
          onClose={onA2PModalClose}
          phoneNumber={selectedNumber}
          onComplete={handleA2PRegistrationComplete}
        />
      )}

      <ConnectSMSNumberDialog
        isOpen={isConnectSMSOpen}
        onClose={onConnectSMSClose}
        onSuccess={fetchData}
      />
    </Box>
  );
};

export default SMSPhoneNumbers;
